import { DocumentNode } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { Alert, AlertText } from "@repo/components/src/ui/alert";
import { Box } from "@repo/components/src/ui/box";
import { Card } from "@repo/components/src/ui/card";
import { Heading } from "@repo/components/src/ui/heading";
import { ConfirmTaxProfileDocument } from "@repo/graphql/documents/fiscal/mutation.generated";
import { TaxProfileFragmentFragment } from "@repo/graphql/documents/fiscal/fragment.generated";
import { useState } from "react";

import TaxProfileForm from "../taxProfileForm";
import ConfirmedSummary from "./ConfirmedSummary";
import MissingState from "./MissingState";

type Props = {
  brandId: string;
  countryCode: string;
  countryName: string;
  defaultLegalName: string;
  taxProfile: TaxProfileFragmentFragment | null;
  canEdit: boolean;
  onSaved?: (profile: TaxProfileFragmentFragment) => void;
  refetchQueries?: DocumentNode[];
  /** Surfaces a `userErrors` message from a caller-side mutation (e.g. a
   * payment mutation rejected for a missing/unconfirmed tax profile) without
   * this card owning that mutation itself. */
  errorMessage?: string | null;
};

export default function BrandTaxProfileCard({
  brandId,
  countryCode,
  countryName,
  defaultLegalName,
  taxProfile,
  canEdit,
  onSaved,
  refetchQueries = [],
  errorMessage,
}: Props) {
  const [editing, setEditing] = useState(false);
  const isConfirmedComplete =
    !!taxProfile?.isComplete && !!taxProfile.confirmedAt;
  const showForm = canEdit && (editing || !isConfirmedComplete);

  const [confirmTaxProfile] = useMutation(ConfirmTaxProfileDocument, {
    refetchQueries,
  });

  const handleFormSaved = async (profile: TaxProfileFragmentFragment) => {
    if (profile.isComplete && !profile.confirmedAt) {
      await confirmTaxProfile({ variables: { taxProfileId: profile.id } });
    }
    setEditing(false);
    onSaved?.(profile);
  };

  return (
    <Card variant="outline" size="lg" className="rounded-2xl">
      <Heading size="sm">Datos de facturación</Heading>
      {errorMessage ? (
        <Alert action="error" className="mt-4">
          <AlertText size="sm">{errorMessage}</AlertText>
        </Alert>
      ) : null}
      <Box className="mt-4">
        {showForm ? (
          <TaxProfileForm
            owner={{ kind: "BRAND", brandId }}
            countryCode={countryCode}
            countryName={countryName}
            initial={taxProfile}
            defaultLegalName={defaultLegalName}
            acknowledgment
            submitLabel="Guardar datos de facturación"
            refetchQueries={refetchQueries}
            onSaved={handleFormSaved}
          />
        ) : isConfirmedComplete && taxProfile ? (
          <ConfirmedSummary
            taxProfile={taxProfile}
            countryCode={countryCode}
            canEdit={canEdit}
            onEdit={() => setEditing(true)}
          />
        ) : (
          <MissingState />
        )}
      </Box>
    </Card>
  );
}
